import { useCallback, useState } from "react";
import { useSignInWithEmail, useVerifyEmailOTP } from "@coinbase/cdp-hooks";

type Step = "enter-email" | "enter-otp" | "signed-in";

interface State {
  step: Step;
  email: string | null;
  flowId: string | null;
  isSubmitting: boolean;
  error: string | null;
}

/**
 * Wraps the two-step CDP email OTP flow (`signInWithEmail` -> `verifyEmailOTP`) into a
 * small state machine so the sign-in page just renders `state.step` and calls two
 * functions. Build.md requires email OTP at minimum; this is the only sign-in method
 * implemented in V1.
 */
export function useEmailSignIn() {
  const { signInWithEmail } = useSignInWithEmail();
  const { verifyEmailOTP } = useVerifyEmailOTP();
  const [state, setState] = useState<State>({
    step: "enter-email",
    email: null,
    flowId: null,
    isSubmitting: false,
    error: null,
  });

  const requestOtp = useCallback(
    async (email: string) => {
      setState((s) => ({ ...s, isSubmitting: true, error: null }));
      try {
        const result = await signInWithEmail({ email });
        setState({
          step: "enter-otp",
          email,
          flowId: result.flowId,
          isSubmitting: false,
          error: null,
        });
      } catch (err) {
        setState((s) => ({
          ...s,
          isSubmitting: false,
          error: err instanceof Error ? err.message : "Could not send the sign-in code. Try again.",
        }));
      }
    },
    [signInWithEmail],
  );

  const verifyOtp = useCallback(
    async (otp: string) => {
      if (!state.flowId) {
        setState((s) => ({ ...s, error: "Sign-in session expired. Request a new code." }));
        return;
      }
      setState((s) => ({ ...s, isSubmitting: true, error: null }));
      try {
        await verifyEmailOTP({ flowId: state.flowId, otp });
        setState((s) => ({ ...s, step: "signed-in", isSubmitting: false, error: null }));
      } catch (err) {
        setState((s) => ({
          ...s,
          isSubmitting: false,
          error: err instanceof Error ? err.message : "That code didn't work. Try again.",
        }));
      }
    },
    [state.flowId, verifyEmailOTP],
  );

  const reset = useCallback(() => {
    setState({ step: "enter-email", email: null, flowId: null, isSubmitting: false, error: null });
  }, []);

  return { ...state, requestOtp, verifyOtp, reset };
}
